
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@dopomogai/ui';

import { useCmsDocument, useAnalytics } from '@dopomogai/agent-kit';
import { useUser, useAuthService } from '@dopomogai/supabase-client/react';

type SharedLayoutOptions = {
  headerDocumentId?: string;
  footerDocumentId?: string;
  isPublicSite?: boolean;
};

export function useSharedLayoutData({
  headerDocumentId = 'app-header',
  footerDocumentId = 'app-footer',
  isPublicSite = false,
}: SharedLayoutOptions = {}) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const user = useUser();
  const authService = useAuthService();
  const analytics = useAnalytics();

  const { document: headerDoc } = useCmsDocument(headerDocumentId, i18n.language);
  const { document: footerDoc } = useCmsDocument(footerDocumentId, i18n.language);

  const lastHeaderDoc = useRef<any>(null);
  const lastFooterDoc = useRef<any>(null);

  useEffect(() => {
    if (headerDoc) lastHeaderDoc.current = headerDoc;
  }, [headerDoc]);

  useEffect(() => {
    if (footerDoc) lastFooterDoc.current = footerDoc;
  }, [footerDoc]);

  const effectiveHeaderDoc = headerDoc ?? lastHeaderDoc.current;
  const effectiveFooterDoc = footerDoc ?? lastFooterDoc.current;

  const isLoading = useMemo(
    () => !effectiveHeaderDoc || !effectiveFooterDoc,
    [effectiveHeaderDoc, effectiveFooterDoc],
  );

  const handleSignOut = async () => {
    await authService.logout();
    localStorage.removeItem('dopomogai_signup_finalized');
    navigate('/');
    toast.info('You have been signed out.');
  };

  const handleAction = (actionType: string, payload: any) => {
    if (actionType === 'NAVIGATE_TO_PAGE') {
      analytics.track('cta_click', {
        cta_id: payload?.ctaId || `nav_${String(payload?.path || '').replace(/\//g, '_')}`,
        path: payload?.path,
      });

      const target = payload?.path;
      if (!target) return;

      const inApp =
        typeof window !== 'undefined' && window.location?.pathname?.startsWith('/app');
      if (inApp && target === '/') {
        navigate('/app/dashboard');
        return;
      }

      navigate(target);
      return;
    }

    if (actionType === 'SHOW_MODAL' && payload?.modalType === 'BETA_WAITLIST') {
      analytics.track('cta_click', { cta_id: 'beta_waitlist_modal' });
      navigate('/auth/register');
      return;
    }
  };

  const getAdaptedComponentData = (doc: any) => {
    if (!doc || !doc.content) return undefined;
    return {
      locales: { [i18n.language]: { props: doc.content.props || {} } },
      structure: doc.content.structure || {},
      ...doc,
    };
  };

  const isAuthenticated = !isPublicSite && !!user;
  const safeUser = isAuthenticated ? user : null;
  const safeSignOut = isAuthenticated ? handleSignOut : () => {};

  const headerDataWithAuth = {
    ...getAdaptedComponentData(effectiveHeaderDoc),
    isAuthenticated,
    user: safeUser,
    onSignOut: safeSignOut,
  };

  const footerDataAdapted = getAdaptedComponentData(effectiveFooterDoc);

  return {
    isLoading,
    headerDataWithAuth,
    footerDataAdapted,
    handleAction,
  };
}

export default useSharedLayoutData;
